import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Calendar, Clock, MapPin, IndianRupee, Edit, Send, CheckCircle, MessageCircle, CreditCard, CalendarClock, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { LinkedAmountPercent, amtToPct } from './booking/LinkedAmountPercent';
import { useBooking } from '../contexts/BookingContext';
import { useAuth } from '../contexts/AuthContext';
import { useCustomer } from '../contexts/CustomerContext';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import QRPaymentModal from './QRPaymentModal';
import { QrCode, Copy } from 'lucide-react';
import { generateTimeSlots, formatTime } from '../utils/timeSlots';
import RentalsSection from './RentalsSection';

const TIME_SLOTS = generateTimeSlots(0, 23, 30);

const getSectionSlotType = (section, facility) => {
  // If facility has courts, find the court type
  if (facility?.courts) {
    const court = facility.courts.find(c => c.id === section);
    if (court) {
      return court.court_type || court.sport || section;
    }
  }
  // Fallback for football
  if (section === 'full') return 'full';
  if (section === 'north' || section === 'south') return 'half';
  if (section === '5aside') return '5aside';
  return 'quarter';
};

const BookingDialog = ({ isOpen, onClose, slotInfo, facility, onBookingCreated }) => {
  const { addBooking, updateBooking, cancelBooking, collectPayment } = useBooking();
  const { user, facility: authFacility, isReadOnly, canEditBookings, canCollectPayments } = useAuth();
  const { getCustomerByPhone } = useCustomer();
  
  // Use passed facility or fall back to auth context facility
  const currentFacility = facility || authFacility;
  
  // Debug: Check if SUFC should show
  const isSUFC = currentFacility?.id === 'facility_trp_sports' || 
                 currentFacility?.facility_id === 'facility_trp_sports' ||
                 currentFacility?.name?.toLowerCase()?.includes('south united') ||
                 currentFacility?.name?.toLowerCase()?.includes('sufc') ||
                 currentFacility?.slug === 'trp-sports' ||
                 user?.facility_id === 'facility_trp_sports';

  // Check if BCFC option should show (Sports Web - BCFC venue only)
  const isBCFC = currentFacility?.id === 'facility_sportsweb' ||
                 currentFacility?.facility_id === 'facility_sportsweb' ||
                 currentFacility?.name?.toLowerCase()?.includes('sportsweb') ||
                 currentFacility?.name?.toLowerCase()?.includes('sports web') ||
                 currentFacility?.name?.toLowerCase()?.includes('bcfc') ||
                 currentFacility?.slug === 'sportsweb' ||
                 user?.facility_id === 'facility_sportsweb';
  
  const [mode, setMode] = useState('view');
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    duration: 2,
    paymentStatus: 'pending',
    amountPaid: 0,
    bookingType: 'b2c',
    customRate: 0,
    customRateTouched: false, // false = auto-fill from standard rate; true = admin explicitly set it (including explicit 0)
    discountPercent: 0, // Admin discount for B2C bookings
    discountAmount: 0, // ₹ amount is the money source of truth; % is derived
    onlinePartialAmount: 0, // ₹ to charge now for online partial link
    sendPaymentLink: false,
    paymentMethod: 'offline', // 'online' or 'offline'
    sendWhatsAppConfirmation: true, // Default to send WhatsApp confirmation
    sendEmailConfirmation: true // Default to send email confirmation
  });
  const [collectAmount, setCollectAmount] = useState(0);
  const [collectMethod, setCollectMethod] = useState('cash');
  const [customerFound, setCustomerFound] = useState(false);
  const [settings, setSettings] = useState(null);
  const [pricePreview, setPricePreview] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [fetchingRefs, setFetchingRefs] = useState(false);
  const [localRefs, setLocalRefs] = useState(null);
  
  // Resolve discount state (Playo/Hudle)
  const [discountType, setDiscountType] = useState('');
  const [venueDiscountPct, setVenueDiscountPct] = useState(0);
  const [platformDiscountPct, setPlatformDiscountPct] = useState(0);
  
  // Customer autocomplete state
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await api.getSettings();
        setSettings(data);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (slotInfo?.booking) {
      // Calculate duration from start_time and end_time
      const startTime = slotInfo.booking.startTime || slotInfo.booking.start_time || '';
      const endTime = slotInfo.booking.endTime || slotInfo.booking.end_time || '';
      let calculatedDuration = 2; // default
      
      if (startTime && endTime) {
        const startIndex = TIME_SLOTS.indexOf(startTime);
        const endIndex = TIME_SLOTS.indexOf(endTime);
        if (startIndex !== -1 && endIndex !== -1) {
          calculatedDuration = endIndex - startIndex;
          if (calculatedDuration <= 0) {
            // Handle overnight bookings
            calculatedDuration = (48 - startIndex) + endIndex;
          }
        }
      }
      
      setFormData({
        customerName: slotInfo.booking.customerName || slotInfo.booking.customer_name || '',
        customerEmail: slotInfo.booking.customerEmail || slotInfo.booking.customer_email || '',
        customerPhone: slotInfo.booking.customerPhone || slotInfo.booking.customer_phone || '',
        duration: calculatedDuration,
        paymentStatus: slotInfo.booking.paymentStatus || slotInfo.booking.payment_status || 'pending',
        amountPaid: slotInfo.booking.amountPaid || slotInfo.booking.amount_paid || 0,
        bookingType: slotInfo.booking.bookingType || slotInfo.booking.booking_type || 'b2c',
        customRate: slotInfo.booking.customRate || slotInfo.booking.custom_rate || 0,
        customRateTouched: !!(slotInfo.booking.customRate || slotInfo.booking.custom_rate),
        discountPercent: slotInfo.booking.discount_percent || slotInfo.booking.discountPercent || 0,
        discountAmount: slotInfo.booking.discount_amount || slotInfo.booking.discountAmount || 0,
        onlinePartialAmount: 0,
        sendPaymentLink: false,
        paymentMethod: 'offline',
        offlinePaymentMode: ['cash', 'upi', 'other'].includes(slotInfo.booking.payment_method) ? slotInfo.booking.payment_method : 'cash'
      });
    } else {
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        duration: slotInfo?.duration || 2,
        paymentStatus: 'pending',
        amountPaid: 0,
        bookingType: 'b2c',
        customRate: 0,
        customRateTouched: false,
        discountPercent: 0,
        discountAmount: 0,
        onlinePartialAmount: 0,
        sendPaymentLink: false,
        paymentMethod: 'offline',
        offlinePaymentMode: 'cash',
        sendWhatsAppConfirmation: true,
        sendEmailConfirmation: true
      });
    }
    
    setCustomerFound(false);
    setCollectAmount(0);
    
    if (slotInfo?.status === 'available') {
      setMode('create');
    } else {
      setMode('view');
    }
  }, [slotInfo]);

  // Final total the customer owes = rack rate − discount. Partial payment is a slice of this.
  const finalTotal = (formData.bookingType === 'b2c' && formData.discountAmount > 0) ? discountedPrice : pricePreview;

  // Keep entered amounts valid when the base changes (e.g. discount lowers the final total).
  useEffect(() => {
    setFormData((fd) => {
      if (!pricePreview || pricePreview <= 0) return fd; // price not loaded yet — don't wipe prefilled edit values
      let next = fd;
      if ((fd.discountAmount || 0) > pricePreview) next = { ...next, discountAmount: pricePreview };
      if ((fd.onlinePartialAmount || 0) > finalTotal) next = { ...next, onlinePartialAmount: finalTotal };
      if ((fd.amountPaid || 0) > finalTotal) next = { ...next, amountPaid: finalTotal };
      return next;
    });
  }, [finalTotal, pricePreview]);

  // Calculate price preview when relevant fields change
  useEffect(() => {
    const calculatePrice = async () => {
      if (!slotInfo) return;
      
      const startTime = mode === 'edit' ? (slotInfo.booking?.startTime || slotInfo.booking?.start_time) : slotInfo.time;
      const timeIndex = TIME_SLOTS.indexOf(startTime);
      const endTime = timeIndex + formData.duration < TIME_SLOTS.length
        ? TIME_SLOTS[timeIndex + formData.duration]
        : TIME_SLOTS[(timeIndex + formData.duration) % TIME_SLOTS.length] || '00:00';
      const slotType = getSectionSlotType(slotInfo.section, currentFacility);

      // Always fetch the canonical standard rate from the backend so we
      // never silently default to ₹0 — even for SUFC/BCFC internal booking
      // types. Only an EXPLICIT admin override (customRateTouched=true) can
      // record a non-standard price (including an explicit ₹0 comp).
      const hours = formData.duration * 0.5;
      let standardTotal = 0;
      try {
        const params = {
          slot_type: slotType,
          start_time: startTime,
          end_time: endTime,
          booking_type: 'b2c', // always price at published rate — overrides are explicit only
          date: slotInfo.date || formData.date || new Date().toISOString().split('T')[0]
        };
        const result = await api.calculatePrice(params);
        standardTotal = result.total || 0;
      } catch (error) {
        console.error('Error calculating price:', error);
        // Fallback: settings-driven peak/off-peak sum. Still never hardcodes to 0.
        const pricing = settings?.pricing || { full: { peak: 5000, off_peak: 4000 }, half: { peak: 2800, off_peak: 2200 }, quarter: { peak: 1500, off_peak: 1200 } };
        const peakHours = settings?.peak_hours || { start: 17, end: 23 };
        for (let i = 0; i < formData.duration; i++) {
          const slotTime = TIME_SLOTS[timeIndex + i];
          if (slotTime) {
            const hour = parseInt(slotTime.split(':')[0]);
            const isPeak = hour >= peakHours.start && hour < peakHours.end;
            const price = pricing[slotType]?.[isPeak ? 'peak' : 'off_peak'] || 0;
            standardTotal += price / 2;
          }
        }
      }

      // For internal-comp / B2B / event: admin can override the standard
      // rate. If untouched we use the standard rate as the DEFAULT (never 0).
      // If explicitly touched (even to 0), respect it — that is a
      // deliberate comp.
      // Auto-prime: only for paid-override types (b2b, event) do we fetch
      // the standard rate as the default so admin doesn't silently record
      // ₹0 for a corporate booking. Internal comp types (sufc, bcfc) are
      // ALWAYS ₹0 by default — that's what the dropdown label promises
      // ("SUFC (Internal - ₹0)"). Admin can still type a value to override.
      const COMP_TYPES = ['sufc', 'bcfc'];
      const PAID_OVERRIDE_TYPES = ['b2b', 'event'];
      const isOverrideType = PAID_OVERRIDE_TYPES.includes(formData.bookingType)
                             || COMP_TYPES.includes(formData.bookingType);
      if (isOverrideType && formData.customRateTouched) {
        const basePrice = (formData.customRate || 0) * hours;
        setPricePreview(basePrice);
        setDiscountedPrice(basePrice);
        return;
      }
      if (isOverrideType && !formData.customRateTouched) {
        // Pre-fill the rate for PAID override types only (B2B / event) so
        // admin doesn't accidentally save ₹0 for a corporate booking.
        // SUFC/BCFC are comp types — stay at ₹0 as the dropdown promises.
        if (PAID_OVERRIDE_TYPES.includes(formData.bookingType)) {
          const stdHourly = hours > 0 ? standardTotal / hours : 0;
          if (Math.abs((formData.customRate || 0) - stdHourly) > 0.01) {
            setFormData(prev => ({ ...prev, customRate: stdHourly }));
          }
          setPricePreview(standardTotal);
          setDiscountedPrice(standardTotal);
        } else {
          // Comp types (sufc/bcfc): default ₹0.
          setPricePreview(0);
          setDiscountedPrice(0);
        }
        return;
      }

      // B2C — apply admin discount (₹) to the standard total.
      setPricePreview(standardTotal);
      if (formData.bookingType === 'b2c' && formData.discountAmount > 0) {
        const discAmt = Math.min(formData.discountAmount, standardTotal);
        setDiscountedPrice(standardTotal - discAmt);
      } else {
        setDiscountedPrice(standardTotal);
      }
    };
    
    calculatePrice();
  }, [slotInfo, formData.duration, formData.bookingType, formData.customRate, formData.customRateTouched, formData.discountAmount, settings, mode, currentFacility]);

  // Phone lookup
  useEffect(() => {
    const lookupCustomer = async () => {
      if (formData.customerPhone && formData.customerPhone.length === 10 && mode === 'create') {
        try {
          const customer = await getCustomerByPhone(formData.customerPhone);
          if (customer && !customerFound) {
            setFormData(prev => ({
              ...prev,
              customerName: customer.name,
              customerEmail: customer.email || ''
            }));
            setCustomerFound(true);
            toast({
              title: "Customer Found",
              description: `Loaded details for ${customer.name}. Total bookings: ${customer.total_bookings}, Total spent: ₹${customer.total_spent}`,
            });
          } else if (!customer && customerFound) {
            setCustomerFound(false);
          }
        } catch (error) {
          if (customerFound) setCustomerFound(false);
        }
      }
    };
    lookupCustomer();
  }, [formData.customerPhone, mode, customerFound, getCustomerByPhone]);

  // Customer name autocomplete search
  useEffect(() => {
    const searchCustomers = async () => {
      if (mode !== 'create' || formData.customerName.length < 2 || customerFound) {
        setCustomerSuggestions([]);
        setShowCustomerDropdown(false);
        return;
      }
      
      setSearchingCustomers(true);
      try {
        const results = await api.searchCustomersByName(formData.customerName);
        setCustomerSuggestions(results || []);
        setShowCustomerDropdown(results && results.length > 0);
      } catch (error) {
        console.error('Customer search error:', error);
        setCustomerSuggestions([]);
      } finally {
        setSearchingCustomers(false);
      }
    };
    
    const debounceTimer = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [formData.customerName, mode, customerFound]);

  // Handle customer selection from dropdown
  const handleSelectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customerName: customer.name || '',
      customerEmail: customer.email || '',
      customerPhone: customer.phone || ''
    }));
    setCustomerFound(true);
    setShowCustomerDropdown(false);
    setCustomerSuggestions([]);
    toast({
      title: "Customer Selected",
      description: `Loaded details for ${customer.name}`,
    });
  };

  if (!isOpen) return null;

  const actualSlotType = getSectionSlotType(slotInfo.section, currentFacility);
  
  // Get court name from facility if available
  const courtName = currentFacility?.courts?.find(c => c.id === slotInfo.section)?.name || slotInfo.section;

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const timeIndex = TIME_SLOTS.indexOf(slotInfo.time);
      const endTime = timeIndex + formData.duration < TIME_SLOTS.length
        ? TIME_SLOTS[timeIndex + formData.duration]
        : TIME_SLOTS[(timeIndex + formData.duration) % TIME_SLOTS.length] || '00:00';
      
      // Determine if we're sending payment link (online method)
      const isOnlinePayment = formData.paymentMethod === 'online';
      
      // Use discounted price for B2C bookings with discount
      const finalAmount = formData.bookingType === 'b2c' && formData.discountAmount > 0 
        ? discountedPrice 
        : pricePreview;

      // Safety guard: never let a PAID override type (b2b/event) record
      // ₹0 unless admin explicitly typed 0. Comp types (sufc/bcfc) are
      // ₹0 by default — nothing to guard.
      const PAID_OVERRIDE_TYPES = ['b2b', 'event'];
      if (PAID_OVERRIDE_TYPES.includes(formData.bookingType)
          && (finalAmount || 0) <= 0 && !formData.customRateTouched) {
        toast({
          title: 'Rate not set',
          description: 'Standard rate could not be applied. Enter a rate in ₹/hour (use 0 only for an explicit comp).',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
      
      // If sending payment link, don't mark as paid
      const amountPaid = isOnlinePayment ? 0 :
                         formData.paymentStatus === 'paid' ? finalAmount : 
                         (formData.paymentStatus === 'partial' || formData.paymentStatus === 'monthly') ? Math.min(formData.amountPaid, finalAmount) : 0;

      // Discount amount recorded (₹ is the source of truth)
      const discountAmount = formData.bookingType === 'b2c' && formData.discountAmount > 0
        ? Math.min(formData.discountAmount, pricePreview)
        : null;

      const bookingData = {
        facility_id: currentFacility?.id || null,
        date: slotInfo.date,
        start_time: slotInfo.time,
        end_time: endTime,
        slot_type: actualSlotType,
        section: slotInfo.section === 'full' ? null : slotInfo.section,
        court_id: slotInfo.section,  // Store the court ID
        sport: currentFacility?.courts?.find(c => c.id === slotInfo.section)?.sport || 'football',
        customer_name: formData.customerName,
        customer_email: formData.customerEmail || null,
        customer_phone: formData.customerPhone,
        booking_type: formData.bookingType,
        source: 'direct',  // Admin bookings are always 'direct'
        custom_rate: (['b2b', 'event', 'sufc', 'bcfc'].includes(formData.bookingType) && formData.customRateTouched) ? formData.customRate : null,
        amount: finalAmount,
        amount_paid: amountPaid,
        // Honour explicit Monthly billing choice from the admin. For all other
        // statuses the backend derives from amount/amount_paid as before.
        payment_status: formData.paymentMethod === 'offline' && formData.paymentStatus === 'monthly'
          ? 'monthly'
          : undefined,
        // Discount fields
        discount_percent: formData.bookingType === 'b2c' && formData.discountAmount > 0 ? amtToPct(Math.min(formData.discountAmount, pricePreview), pricePreview) : null,
        discount_amount: discountAmount,
        // How offline money was collected (cash/upi/other) — feeds finance reconciliation
        payment_method: (formData.paymentMethod === 'offline' && ['paid', 'partial', 'monthly'].includes(formData.paymentStatus)) ? formData.offlinePaymentMode : undefined,
        // Email notification flag
        send_confirmation_email: formData.sendEmailConfirmation,
        // When this dialog is opened from the Community Games "Book this Slot"
        // action, the slot info carries the game id — forward it so the
        // booking record is stamped and the community game's own blocked_slot
        // does NOT count as a conflict.
        community_game_id: slotInfo?.community_game_id || null,
      };

      const newBooking = await addBooking(bookingData);
      
      // Send payment link if online payment method selected
      if (isOnlinePayment && pricePreview > 0) {
        // Partial: charge the chosen amount now; the booking amount stays the full charge.
        const linkAmount = (formData.onlinePayType === 'partial' && formData.onlinePartialAmount > 0)
          ? Math.min(formData.onlinePartialAmount, finalAmount)
          : finalAmount;
        try {
          const linkResult = await api.createPaymentLink({
            booking_id: newBooking.id,
            amount: linkAmount,
            customer_name: formData.customerName,
            customer_email: formData.customerEmail || null,
            customer_phone: formData.customerPhone,
            description: linkAmount < finalAmount
              ? `Partial (${amtToPct(linkAmount, finalAmount)}%) payment for booking on ${slotInfo.date}`
              : `Payment for booking on ${slotInfo.date}`
          });
          
          toast({
            title: "Booking Created & Payment Link Sent!",
            description: (
              <div className="space-y-1">
                <p>Booking ID: {newBooking.id}</p>
                <p>Payment link sent to {formData.customerPhone}</p>
                <p className="text-xs text-gray-500 break-all">{linkResult.short_url}</p>
              </div>
            ),
          });
        } catch (linkError) {
          toast({
            title: "Booking Created (Payment Link Failed)",
            description: `Booking created but payment link failed: ${linkError.message}`,
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Booking Created Successfully!",
          description: (
            <div className="space-y-1">
              <p>Booking ID: {newBooking.id}</p>
              <p>Customer: {formData.customerName}</p>
              {amountPaid === 0 && <p className="text-yellow-600 text-sm">Payment pending (offline)</p>}
            </div>
          ),
        });
      }
      
      // Send WhatsApp confirmation if enabled
      if (formData.sendWhatsAppConfirmation && formData.customerPhone) {
        try {
          await api.sendBookingConfirmation({
            phone_number: formData.customerPhone,
            customer_name: formData.customerName,
            booking_id: newBooking.id,
            date: slotInfo.date,
            time: `${formatTime(slotInfo.time)} - ${formatTime((() => { const idx = TIME_SLOTS.indexOf(slotInfo.time); return idx + formData.duration < TIME_SLOTS.length ? TIME_SLOTS[idx + formData.duration] : TIME_SLOTS[(idx + formData.duration) % TIME_SLOTS.length] || '00:00'; })())}`,
            slot_type: actualSlotType,
            amount: pricePreview
          });
          toast({
            title: "WhatsApp Sent!",
            description: `Booking confirmation sent to ${formData.customerPhone}`,
          });
        } catch (whatsappError) {
          console.error('WhatsApp error:', whatsappError);
          // Don't show error toast as booking was successful
        }
      }
      
      // Notify caller that a booking was successfully created (used by Community Games to mark game as converted)
      if (typeof onBookingCreated === 'function') {
        try {
          await onBookingCreated(newBooking);
        } catch (cbErr) {
          console.error('onBookingCreated callback error:', cbErr);
        }
      }

      onClose();
    } catch (error) {
      toast({
        title: "Error Creating Booking",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const startTime = slotInfo.booking.startTime || slotInfo.booking.start_time;
      const timeIndex = TIME_SLOTS.indexOf(startTime);
      const endTime = timeIndex + formData.duration < TIME_SLOTS.length
        ? TIME_SLOTS[timeIndex + formData.duration]
        : TIME_SLOTS[(timeIndex + formData.duration) % TIME_SLOTS.length] || '00:00';
      
      // Use pricePreview which is now correctly calculated for B2B/Event with custom rate
      const finalAmount = formData.bookingType === 'b2c' && formData.discountAmount > 0 
        ? discountedPrice 
        : pricePreview;

      // Safety guard: same as create path — no default-₹0 for PAID
      // override types; comp types (sufc/bcfc) are ₹0 by design.
      const PAID_OVERRIDE_TYPES_EDIT = ['b2b', 'event'];
      if (PAID_OVERRIDE_TYPES_EDIT.includes(formData.bookingType)
          && (finalAmount || 0) <= 0 && !formData.customRateTouched) {
        toast({
          title: 'Rate not set',
          description: 'Standard rate could not be applied. Enter a rate in ₹/hour (use 0 only for an explicit comp).',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
      
      const isOnlinePayment = formData.paymentMethod === 'online';
      const existingPaid = slotInfo.booking.amount_paid ?? slotInfo.booking.amountPaid ?? 0;
      // For an online switch we do NOT mark the booking paid — the customer pays via the
      // link and the payment_link.paid webhook settles amount_paid. Keep existing paid.
      const amountPaid = isOnlinePayment ? existingPaid :
                         formData.paymentStatus === 'paid' ? finalAmount :
                         (formData.paymentStatus === 'partial' || formData.paymentStatus === 'monthly') ? Math.min(formData.amountPaid, finalAmount) : 0;

      await updateBooking(slotInfo.booking.id, {
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        end_time: endTime,
        booking_type: formData.bookingType,
        custom_rate: (['b2b', 'event', 'sufc', 'bcfc'].includes(formData.bookingType) && formData.customRateTouched) ? formData.customRate : null,
        amount: finalAmount,
        amount_paid: amountPaid,
        // Send payment_status only when admin picked 'monthly' so backend
        // honours the override instead of auto-deriving.
        payment_status: (!isOnlinePayment && formData.paymentStatus === 'monthly') ? 'monthly' : undefined,
        discount_percent: formData.bookingType === 'b2c' && formData.discountAmount > 0 ? amtToPct(Math.min(formData.discountAmount, pricePreview), pricePreview) : 0,
        discount_amount: formData.bookingType === 'b2c' && formData.discountAmount > 0 
          ? Math.min(formData.discountAmount, pricePreview) 
          : 0,
        payment_method: (!isOnlinePayment && ['paid', 'partial', 'monthly'].includes(formData.paymentStatus)) ? formData.offlinePaymentMode : undefined
      });

      // If admin switched to Online, generate + send the Razorpay payment link (full or partial).
      if (isOnlinePayment && finalAmount > 0) {
        const linkAmount = (formData.onlinePayType === 'partial' && formData.onlinePartialAmount > 0)
          ? Math.min(formData.onlinePartialAmount, finalAmount)
          : finalAmount;
        try {
          const linkResult = await api.createPaymentLink({
            booking_id: slotInfo.booking.id,
            amount: linkAmount,
            customer_name: formData.customerName,
            customer_email: formData.customerEmail || null,
            customer_phone: formData.customerPhone,
            description: linkAmount < finalAmount
              ? `Partial (${amtToPct(linkAmount, finalAmount)}%) payment for booking`
              : `Payment for booking`
          });
          toast({
            title: "Payment Link Sent!",
            description: (
              <div className="space-y-1">
                <p>Link sent to {formData.customerPhone}</p>
                <p className="text-xs text-gray-500 break-all">{linkResult.short_url}</p>
              </div>
            ),
          });
        } catch (linkError) {
          toast({
            title: "Booking Updated (Payment Link Failed)",
            description: `Booking updated but payment link failed: ${linkError.message}`,
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Booking Updated",
          description: `Booking for ${formData.customerName} has been updated successfully.`,
        });
      }
      onClose();
    } catch (error) {
      toast({
        title: "Error Updating Booking",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCollectPayment = async () => {
    if (!collectAmount || collectAmount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid amount to collect.",
        variant: "destructive"
      });
      return;
    }

    const currentPending = slotInfo.booking.amountPending || slotInfo.booking.amount_pending || slotInfo.booking.amount;
    
    if (collectAmount > currentPending) {
      toast({
        title: "Invalid Amount",
        description: `Cannot collect more than pending amount (₹${currentPending}).`,
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await collectPayment(slotInfo.booking.id, { amount: parseFloat(collectAmount), payment_method: collectMethod });

      toast({
        title: "Payment Collected",
        description: `₹${collectAmount} collected via ${collectMethod.toUpperCase()}.`,
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error Collecting Payment",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await updateBooking(slotInfo.booking.id, { status: 'confirmed' });
      toast({
        title: "Booking Confirmed",
        description: "The booking has been confirmed.",
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [sendCancellationEmail, setSendCancellationEmail] = useState(true);
  const [refundPercent, setRefundPercent] = useState(100);
  const [refundPreview, setRefundPreview] = useState(null);

  // Reschedule state
  const [reschedForm, setReschedForm] = useState(null);
  const [reschedPreview, setReschedPreview] = useState(null);
  const [reschedLoading, setReschedLoading] = useState(false);
  const [reschedError, setReschedError] = useState('');

  // Any booking with money collected (online and/or cash) can be refunded on cancel.
  const cancelBookingRef = slotInfo?.booking || {};
  const amountPaidForRefund = Number(
    cancelBookingRef.amount_paid || cancelBookingRef.amountPaid || 0
  );
  const hasCollected = amountPaidForRefund > 0;
  const refundAmount = Math.round(amountPaidForRefund * refundPercent) / 100;

  const handleCancel = async () => {
    setRefundPercent(100); // reset on every open
    setRefundPreview(null);
    setShowCancelDialog(true);
    if (amountPaidForRefund > 0 && slotInfo?.booking?.id) {
      try {
        const preview = await api.request(`/bookings/${slotInfo.booking.id}/refund-preview`);
        setRefundPreview(preview);
      } catch (e) { /* admin-only; ignore if not permitted */ }
    }
  };

  const confirmCancellation = async () => {
    setLoading(true);
    try {
      const effectiveRefundPercent = hasCollected ? refundPercent : 0;
      const result = await cancelBooking(
        slotInfo.booking.id,
        sendCancellationEmail,
        effectiveRefundPercent,
      );
      const refund = result?.refund;
      if (refund?.refund_status === 'failed') {
        toast({
          title: "Cancelled, but refund failed",
          description: `Reconcile in Razorpay dashboard. Error: ${refund.refund_error || 'unknown'}`,
          variant: "destructive",
        });
      } else if (refund?.refund_status === 'processed' || refund?.refund_status === 'partial_failed') {
        const manual = Number(refund.refund_manual_cash_pending || 0);
        toast({
          title: "Booking Cancelled & Refunded",
          description: `₹${refund.refund_online || 0} refunded via Razorpay${manual > 0 ? ` · ₹${manual} to hand back as CASH manually` : ''}.`,
        });
      } else {
        toast({
          title: "Booking Cancelled",
          description: sendCancellationEmail
            ? "The booking has been cancelled and customer notified."
            : "The booking has been cancelled.",
          variant: "destructive"
        });
      }
      setShowCancelDialog(false);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Get booking details (handle snake_case from API)
  const booking = slotInfo?.booking || {};
  const bookingDetails = {
    customerName: booking.customerName || booking.customer_name || '',
    customerEmail: booking.customerEmail || booking.customer_email || '',
    customerPhone: booking.customerPhone || booking.customer_phone || '',
    startTime: booking.startTime || booking.start_time || '',
    endTime: booking.endTime || booking.end_time || '',
    slotType: booking.slotType || booking.slot_type || '',
    bookingType: booking.bookingType || booking.booking_type || 'b2c',
    paymentStatus: booking.paymentStatus || booking.payment_status || 'pending',
    paymentMethod: booking.paymentMethod || booking.payment_method || null,
    status: booking.status || 'confirmed',
    amount: booking.amount || 0,
    amountPaid: booking.amountPaid || booking.amount_paid || 0,
    amountPending: booking.amountPending || booking.amount_pending || 0,
    customRate: booking.customRate || booking.custom_rate || 0,
    source: booking.source || 'direct',
    bookedByName: booking.booked_by_name || booking.bookedByName || null,
    createdAt: booking.created_at || booking.createdAt || null,
    rackRate: booking.rack_rate || booking.rackRate || 0,
    venueDiscountPercent: booking.venue_discount_percent || booking.venueDiscountPercent || 0,
    platformDiscountPercent: booking.platform_discount_percent || booking.platformDiscountPercent || 0,
    id: booking.id || booking.bookingId || booking.booking_id,
    razorpay_payment_id: (localRefs?.razorpay_payment_id) || booking.razorpay_payment_id || booking.razorpayPaymentId,
    razorpay_order_id: (localRefs?.razorpay_order_id) || booking.razorpay_order_id || booking.razorpayOrderId,
    razorpay_bank_rrn: (localRefs?.razorpay_bank_rrn) || booking.razorpay_bank_rrn || booking.razorpayBankRrn,
    payment_method: (localRefs?.payment_method) || booking.payment_method || booking.paymentMethod,
    payment_allocations: booking.payment_allocations || booking.paymentAllocations || [],
    refund_amount: booking.refund_amount || booking.refundAmount || 0,
    refund_status: booking.refund_status || booking.refundStatus || '',
    refund_online: booking.refund_online || 0,
    refund_manual_cash_pending: booking.refund_manual_cash_pending || 0,
    refund_processed_at: booking.refund_processed_at || null,
    refund_processed_by: booking.refund_processed_by || null
  };

  // ----- Reschedule (admin/superadmin only; aggregator bookings blocked) -----
  const isAggregatorBooking = ['playo', 'hudle'].includes(bookingDetails.source);
  const canReschedule = canEditBookings
    && ['admin', 'superadmin'].includes(user?.role)
    && !isAggregatorBooking
    && bookingDetails.status !== 'cancelled';

  const reschedEndTime = (() => {
    if (!reschedForm) return '';
    const idx = TIME_SLOTS.indexOf(reschedForm.startTime);
    if (idx === -1) return '';
    const e = idx + (reschedForm.duration || 0);
    return e < TIME_SLOTS.length ? TIME_SLOTS[e] : (TIME_SLOTS[e % TIME_SLOTS.length] || '00:00');
  })();
  const reschedSlotType = reschedForm ? getSectionSlotType(reschedForm.courtId, currentFacility) : '';
  const reschedSection = reschedForm && reschedForm.courtId !== 'full' ? reschedForm.courtId : null;

  const buildReschedPayload = () => ({
    new_date: reschedForm.date,
    new_start_time: reschedForm.startTime,
    new_end_time: reschedEndTime,
    new_court_id: reschedForm.courtId,
    new_slot_type: reschedSlotType,
    new_section: reschedSection,
  });

  const openReschedule = () => {
    const b = slotInfo.booking;
    const start = b.start_time || b.startTime || slotInfo.time || '';
    const end = b.end_time || b.endTime || '';
    const si = TIME_SLOTS.indexOf(start), ei = TIME_SLOTS.indexOf(end);
    let dur = ei - si;
    if (dur <= 0) dur = (TIME_SLOTS.length - si) + ei;
    setReschedForm({
      date: b.date || slotInfo.date || '',
      courtId: b.court_id || b.section || slotInfo.section || 'full',
      startTime: start || TIME_SLOTS[12], duration: dur > 0 ? dur : 2,
      reason: '', notify: false, message: '',
    });
    setReschedPreview(null);
    setReschedError('');
    setMode('reschedule');
  };

  const cleanErr = (msg) => {
    if (!msg) return 'Something went wrong. Please try again.';
    const s = String(msg).trim();
    if (s.startsWith('[') || s.startsWith('{')) return 'Please pick a valid date, court and time.';
    return s;
  };

  const runReschedPreview = async () => {
    if (!reschedForm || !reschedForm.date || !reschedEndTime) return;
    setReschedLoading(true);
    setReschedError('');
    try {
      const res = await api.request(`/bookings/${slotInfo.booking.id}/reschedule/preview`, {
        method: 'POST', body: JSON.stringify(buildReschedPayload()),
      });
      setReschedPreview(res);
    } catch (e) {
      setReschedPreview(null);
      setReschedError(cleanErr(e.message));
    } finally {
      setReschedLoading(false);
    }
  };

  const confirmReschedule = async () => {
    if (!reschedPreview) return;
    if (!reschedPreview.available) { setReschedError('Target slot is not available'); return; }
    if (reschedPreview.reason_required && !reschedForm.reason.trim()) {
      setReschedError('A reason is required when the booking date changes'); return;
    }
    setReschedLoading(true);
    setReschedError('');
    try {
      await api.request(`/bookings/${slotInfo.booking.id}/reschedule`, {
        method: 'POST',
        body: JSON.stringify({
          ...buildReschedPayload(),
          reason: reschedForm.reason,
          notify: reschedForm.notify,
          message: reschedForm.message,
        }),
      });
      toast({
        title: 'Booking Rescheduled',
        description: reschedForm.notify && bookingDetails.customerEmail
          ? 'Moved successfully and the customer has been notified.'
          : 'Moved successfully.',
      });
      onClose();
    } catch (e) {
      setReschedError(cleanErr(e.message));
      toast({ title: 'Reschedule failed', description: cleanErr(e.message), variant: 'destructive' });
    } finally {
      setReschedLoading(false);
    }
  };

  // Auto-refresh the preview whenever the target changes while rescheduling.
  useEffect(() => {
    if (mode !== 'reschedule' || !reschedForm) return;
    const t = setTimeout(() => { runReschedPreview(); }, 250);
    return () => clearTimeout(t);
  }, [mode, reschedForm?.date, reschedForm?.courtId, reschedForm?.startTime, reschedForm?.duration]);

  // All ONLINE (Razorpay) collections from the ledger — surfaces BOTH references
  // when a booking was paid in parts (e.g. 10% via link + balance via QR).
  const onlinePayments = (bookingDetails.payment_allocations || []).filter(
    a => a && a.type !== 'refund' && (a.court_allocated || 0) > 0 && a.razorpay_payment_id
  );

  // Full itemized collection trail (Razorpay + offline cash/UPI/other), newest data as stored.
  const fmtTrailDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };
  const paymentTrail = (bookingDetails.payment_allocations || [])
    .filter(a => a && a.type !== 'refund' && ((a.court_allocated != null ? a.court_allocated : a.total_amount) || 0) > 0)
    .map((a) => {
      const amt = a.court_allocated != null ? a.court_allocated : (a.total_amount || 0);
      const pm = (a.payment_method || '').toLowerCase();
      const isOnline = !!a.razorpay_payment_id || pm === 'online';
      const label = isOnline ? 'Razorpay' : (pm === 'cash' || pm === 'cash_offline' ? 'Offline Cash' : pm === 'upi' ? 'Offline UPI' : 'Offline');
      const rawBy = a.collected_by_name || a.collected_by || '';
      const by = (rawBy && rawBy.toLowerCase() !== label.toLowerCase()) ? rawBy : ''; // avoid "Razorpay · Razorpay"
      return { amt, label, isOnline, ref: a.razorpay_payment_id, by, at: a.event_at };
    });
  // Reconcile with amount_paid: legacy bookings may carry paid money with no ledger entries.
  const _allocSum = paymentTrail.reduce((s, p) => s + (p.amt || 0), 0);
  const _residual = Math.round((Number(bookingDetails.amountPaid || 0) - _allocSum) * 100) / 100;
  if (paymentTrail.length > 0 && _residual > 0.01) {
    paymentTrail.unshift({ amt: _residual, label: 'Earlier payment', isOnline: false, ref: null, by: '', at: null });
  }
  const trailTotal = paymentTrail.reduce((s, p) => s + (p.amt || 0), 0);
  // Any online (Razorpay) collection present? Used to hide the offline "Fetch reference" link.
  const hasOnlineAlloc = (bookingDetails.payment_allocations || []).some(a => a && a.razorpay_payment_id);

  const handleFetchRefs = async () => {
    setFetchingRefs(true);
    try {
      const res = await api.backfillRazorpayRefs({ booking_id: bookingDetails.id });
      if (res.repaired_count > 0) {
        const fresh = await api.getBooking(bookingDetails.id);
        setLocalRefs({
          razorpay_payment_id: fresh.razorpay_payment_id,
          razorpay_order_id: fresh.razorpay_order_id,
          razorpay_bank_rrn: fresh.razorpay_bank_rrn,
          payment_method: fresh.payment_method,
        });
        toast({ title: 'Reference fetched', description: 'Razorpay payment reference updated.' });
        onBookingCreated?.();
      } else {
        toast({ title: 'No reference found', description: 'Could not locate a captured Razorpay payment for this booking.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setFetchingRefs(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">
            {mode === 'create' && 'Create Booking'}
            {mode === 'edit' && 'Edit Booking'}
            {mode === 'view' && 'Booking Details'}
            {mode === 'collectPayment' && 'Collect Payment'}
            {mode === 'sendPaymentLink' && 'Send Payment Link'}
            {mode === 'resolveDiscount' && 'Resolve Discount'}
            {mode === 'reschedule' && 'Reschedule Booking'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6">
          {/* Closed-QR bounce banner — customer paid on an already-closed QR; Razorpay auto-refunded. Collect manually. */}
          {slotInfo.booking?.qr_payment_bounced && (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4" data-testid="qr-bounce-banner">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
              <div className="text-sm text-red-800">
                <p className="font-semibold">QR payment bounced — collect manually</p>
                <p className="mt-1">
                  {slotInfo.booking.qr_bounce_message ||
                    `A customer attempted to pay via QR but the QR was already closed. Payment of ₹${slotInfo.booking.qr_bounce_amount ?? ''} was auto-refunded by Razorpay. Please collect payment manually.`}
                </p>
              </div>
            </div>
          )}

          {/* Slot Info */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-2">
            <div className="flex items-center text-sm">
              <Calendar className="h-4 w-4 mr-2 text-gray-500" />
              <span>{new Date(slotInfo.date).toLocaleDateString('en-IN')}</span>
            </div>
            <div className="flex items-center text-sm">
              <Clock className="h-4 w-4 mr-2 text-gray-500" />
              <span>{formatTime(mode === 'create' ? slotInfo.time : (bookingDetails?.startTime || slotInfo.booking?.startTime || slotInfo.booking?.start_time || slotInfo.time))}</span>
            </div>
            <div className="flex items-center text-sm">
              <MapPin className="h-4 w-4 mr-2 text-gray-500" />
              <span className="capitalize">{courtName} ({actualSlotType})</span>
            </div>
          </div>

          {/* Create/Edit Form */}
          {(mode === 'create' || mode === 'edit') && (
            <form onSubmit={mode === 'create' ? handleCreateBooking : handleEditBooking} className="space-y-4">
              <div>
                <Label htmlFor="bookingType">Booking Type</Label>
                <select
                  id="bookingType"
                  className="w-full p-2 border rounded"
                  value={formData.bookingType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const isInternal = newType === 'sufc' || newType === 'bcfc';
                    setFormData({ 
                      ...formData, 
                      bookingType: newType,
                      // Auto-set amount to 0 for SUFC / BCFC internal bookings
                      amountPaid: isInternal ? 0 : formData.amountPaid,
                      paymentStatus: isInternal ? 'paid' : formData.paymentStatus
                    });
                  }}
                >
                  <option value="b2c">B2C (Individual Customer)</option>
                  <option value="b2b">B2B (Corporate/Bulk)</option>
                  <option value="event">Event (Custom Rate)</option>
                  {/* SUFC option - only shown for SUFC facility (South United Football Club) */}
                  {isSUFC && (
                    <option value="sufc">SUFC (Internal - ₹0)</option>
                  )}
                  {/* BCFC option - only shown for Sports Web - BCFC venue */}
                  {isBCFC && (
                    <option value="bcfc">BCFC (Internal - ₹0)</option>
                  )}
                </select>
                {formData.bookingType === 'sufc' && (
                  <p className="text-xs text-blue-600 mt-1 font-semibold">
                    SUFC internal booking - Amount will be ₹0
                  </p>
                )}
                {formData.bookingType === 'bcfc' && (
                  <p className="text-xs text-blue-600 mt-1 font-semibold">
                    BCFC internal booking - Amount will be ₹0
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="customerPhone">Phone (10 digits)</Label>
                <Input
                  id="customerPhone"
                  type="tel"
                  required
                  pattern="[0-9]{10}"
                  maxLength="10"
                  value={formData.customerPhone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, customerPhone: value });
                  }}
                  placeholder="9876543210"
                />
                {customerFound && (
                  <p className="text-xs text-green-600 mt-1 font-semibold">✓ Existing customer - details auto-loaded</p>
                )}
              </div>
              <div className="relative">
                <Label htmlFor="customerName">Customer Name</Label>
                <Input
                  id="customerName"
                  required
                  value={formData.customerName}
                  onChange={(e) => {
                    setFormData({ ...formData, customerName: e.target.value });
                    if (customerFound && e.target.value !== formData.customerName) {
                      setCustomerFound(false);
                    }
                  }}
                  onFocus={() => {
                    if (customerSuggestions.length > 0) {
                      setShowCustomerDropdown(true);
                    }
                  }}
                  onBlur={() => {
                    // Delay closing to allow click on dropdown items
                    setTimeout(() => setShowCustomerDropdown(false), 200);
                  }}
                  placeholder="Start typing customer name..."
                  autoComplete="off"
                />
                {customerFound && (
                  <p className="text-xs text-green-600 mt-1 font-semibold">✓ Existing customer selected</p>
                )}
                {searchingCustomers && (
                  <p className="text-xs text-gray-500 mt-1">Searching...</p>
                )}
                {/* Customer Autocomplete Dropdown */}
                {showCustomerDropdown && customerSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                    {customerSuggestions.map((customer, index) => (
                      <div
                        key={customer.id || index}
                        className="px-3 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelectCustomer(customer);
                        }}
                      >
                        <div className="font-medium text-gray-900">{customer.name}</div>
                        <div className="text-xs text-gray-500 flex gap-2">
                          <span>{customer.phone}</span>
                          {customer.email && <span>• {customer.email}</span>}
                          {customer.total_bookings > 0 && (
                            <span className="text-green-600">• {customer.total_bookings} bookings</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="customerEmail">Email</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  placeholder="customer@example.com"
                />
              </div>
              <div>
                <Label htmlFor="duration">Duration (30 min slots)</Label>
                <select
                  id="duration"
                  className="w-full p-2 border rounded"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                >
                  <option value="1">30 minutes</option>
                  <option value="2">1 hour</option>
                  <option value="3">1.5 hours</option>
                  <option value="4">2 hours</option>
                  <option value="5">2.5 hours</option>
                  <option value="6">3 hours</option>
                  <option value="7">3.5 hours</option>
                  <option value="8">4 hours</option>
                  <option value="9">4.5 hours</option>
                  <option value="10">5 hours</option>
                  <option value="11">5.5 hours</option>
                  <option value="12">6 hours</option>
                  <option value="13">6.5 hours</option>
                  <option value="14">7 hours</option>
                  <option value="16">8 hours</option>
                  <option value="20">10 hours</option>
                  <option value="24">12 hours</option>
                </select>
              </div>
              {['b2b', 'event', 'sufc', 'bcfc'].includes(formData.bookingType) && (
                <div>
                  <Label htmlFor="customRate">
                    Rate (₹/hour)
                    {!formData.customRateTouched && ['b2b', 'event'].includes(formData.bookingType)
                      && <span className="text-[10px] font-normal text-gray-500 ml-1">(standard rate — edit to override)</span>}
                    {!formData.customRateTouched && ['sufc', 'bcfc'].includes(formData.bookingType)
                      && <span className="text-[10px] font-normal text-gray-500 ml-1">(default ₹0 — edit to charge)</span>}
                  </Label>
                  <Input
                    id="customRate"
                    type="number"
                    value={formData.customRate}
                    onChange={(e) => setFormData({ ...formData, customRate: parseFloat(e.target.value) || 0, customRateTouched: true })}
                    placeholder="Enter hourly rate"
                    data-testid="booking-rate-input"
                  />
                  {formData.customRateTouched && formData.customRate === 0 && (
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="explicit-comp-note">
                      ⚠ This booking is being recorded at ₹0 (explicit comp). Standard rate override.
                    </p>
                  )}
                </div>
              )}
              {/* Admin Discount for B2C bookings — enter ₹ OR % (linked) */}
              {formData.bookingType === 'b2c' && (
                <div>
                  <Label className="text-sm font-semibold">Discount</Label>
                  <LinkedAmountPercent
                    base={pricePreview}
                    amount={formData.discountAmount}
                    onChange={(amt) => setFormData({ ...formData, discountAmount: amt })}
                    amountLabel="Discount (₹)"
                    percentLabel="Discount (%)"
                    maxPercent={100}
                    testIdPrefix="discount"
                  />
                  {formData.discountAmount > 0 && (
                    <p className="text-sm text-[#0A2C93] font-medium mt-1">
                      Save ₹{Math.min(formData.discountAmount, pricePreview)} · Final total ₹{discountedPrice}
                    </p>
                  )}
                </div>
              )}
              {/* Payment Collection Method */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Payment Collection Method</Label>
                <div className="grid grid-cols-2 gap-3">
                  {/* Online Payment Option */}
                  <div 
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.paymentMethod === 'online' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => setFormData({ 
                      ...formData, 
                      paymentMethod: 'online',
                      sendPaymentLink: true,
                      paymentStatus: 'pending'
                    })}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.paymentMethod === 'online' ? 'border-blue-500' : 'border-gray-400'
                      }`}>
                        {formData.paymentMethod === 'online' && (
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        )}
                      </div>
                      <span className="font-medium text-sm">Online</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      <Send className="inline h-3 w-3 mr-1" />
                      Payment link via SMS
                    </p>
                  </div>
                  
                  {/* Offline Payment Option */}
                  <div 
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.paymentMethod === 'offline' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                    onClick={() => setFormData({ 
                      ...formData, 
                      paymentMethod: 'offline',
                      sendPaymentLink: false
                    })}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.paymentMethod === 'offline' ? 'border-green-500' : 'border-gray-400'
                      }`}>
                        {formData.paymentMethod === 'offline' && (
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        )}
                      </div>
                      <span className="font-medium text-sm">Offline</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      <IndianRupee className="inline h-3 w-3 mr-1" />
                      Cash / UPI / Other
                    </p>
                  </div>
                </div>

                {/* Online: Full vs Partial payment link */}
                {formData.paymentMethod === 'online' && (
                  <div className="mt-3 space-y-2" data-testid="admin-online-paytype">
                    <Label className="text-xs">Payment amount</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        data-testid="admin-pay-full"
                        onClick={() => setFormData({ ...formData, onlinePayType: 'full' })}
                        className={`p-2 border-2 rounded-lg text-sm font-medium ${formData.onlinePayType !== 'partial' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                      >Full</button>
                      <button
                        type="button"
                        data-testid="admin-pay-partial"
                        onClick={() => setFormData({ ...formData, onlinePayType: 'partial', onlinePartialAmount: formData.onlinePartialAmount || Math.round(finalTotal * (currentFacility?.partial_payment?.customer_percentage || 25) / 100) })}
                        className={`p-2 border-2 rounded-lg text-sm font-medium ${formData.onlinePayType === 'partial' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                      >Partial</button>
                    </div>
                    {formData.onlinePayType === 'partial' && (
                      <LinkedAmountPercent
                        base={finalTotal}
                        amount={formData.onlinePartialAmount}
                        onChange={(amt) => setFormData({ ...formData, onlinePartialAmount: amt })}
                        amountLabel="Amount to pay now (₹)"
                        percentLabel="Percentage (%)"
                        maxPercent={99}
                        minAmount={Math.round(finalTotal * (currentFacility?.partial_payment?.min_admin_percentage || 10) / 100)}
                        invalidHint={`Minimum is ${currentFacility?.partial_payment?.min_admin_percentage || 10}% (₹${Math.round(finalTotal * (currentFacility?.partial_payment?.min_admin_percentage || 10) / 100)}).`}
                        showBalance
                        balanceLabel="due at venue"
                        testIdPrefix="admin-partial"
                      />
                    )}
                    <p className="text-xs text-gray-500">Min {currentFacility?.partial_payment?.min_admin_percentage || 10}%. Link is sent for the amount above; balance collected later.</p>
                  </div>
                )}

                
                {/* Offline Payment Status */}
                {formData.paymentMethod === 'offline' && (
                  <div className="mt-2">
                    <Label htmlFor="paymentStatus" className="text-xs">Payment Status</Label>
                    <select
                      id="paymentStatus"
                      className="w-full p-2 border rounded text-sm"
                      value={formData.paymentStatus}
                      onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="partial">Partial</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                )}
                
                {/* How was offline money collected? (feeds finance reconciliation) */}
                {formData.paymentMethod === 'offline' && (formData.paymentStatus === 'paid' || formData.paymentStatus === 'partial' || formData.paymentStatus === 'monthly') && (
                  <div className="mt-2">
                    <Label className="text-xs">Collected via</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      {[['cash', 'Cash'], ['upi', 'UPI'], ['other', 'Other']].map(([val, lbl]) => (
                        <button
                          key={val}
                          type="button"
                          data-testid={`admin-offline-mode-${val}`}
                          onClick={() => setFormData({ ...formData, offlinePaymentMode: val })}
                          className={`p-2 border-2 rounded-lg text-sm font-medium ${formData.offlinePaymentMode === val ? 'border-green-500 bg-green-50 text-[#0A2C93]' : 'border-gray-200 text-gray-600'}`}
                        >{lbl}</button>
                      ))}
                    </div>
                  </div>
                )}

                {formData.paymentMethod === 'offline' && (formData.paymentStatus === 'partial' || formData.paymentStatus === 'monthly') && (
                  <div>
                    <Label className="text-xs font-semibold">Amount paid now</Label>
                    <LinkedAmountPercent
                      base={finalTotal}
                      amount={formData.amountPaid}
                      onChange={(amt) => setFormData({ ...formData, amountPaid: amt })}
                      amountLabel="Amount paid (₹)"
                      percentLabel="Percentage (%)"
                      maxPercent={100}
                      showBalance
                      balanceLabel="due at venue"
                      testIdPrefix="admin-offline-paid"
                    />
                  </div>
                )}
              </div>
              
              {/* Total Amount Display */}
              <div className={`p-3 rounded-lg ${formData.paymentMethod === 'online' ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                {formData.bookingType === 'b2c' && formData.discountAmount > 0 ? (
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500 line-through">
                      Original: ₹{pricePreview}
                    </p>
                    <p className="text-sm font-semibold text-[#0A2C93]">
                      <IndianRupee className="inline h-4 w-4" />
                      Final: ₹{discountedPrice}
                      <span className="ml-2 text-xs bg-[#00D650]/10 text-[#0A2C93] px-2 py-0.5 rounded">
                        {amtToPct(Math.min(formData.discountAmount, pricePreview), pricePreview)}% OFF
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="text-sm font-semibold">
                    <IndianRupee className="inline h-4 w-4" />
                    Total: ₹{pricePreview}
                  </p>
                )}
                {formData.paymentMethod === 'online' && (
                  <p className="text-xs text-blue-600 mt-1">
                    <Send className="inline h-3 w-3 mr-1" />
                    Payment link will be sent to {formData.customerPhone || 'customer phone'}
                  </p>
                )}
                {formData.paymentMethod === 'offline' && formData.paymentStatus === 'pending' && (
                  <p className="text-xs text-yellow-600 mt-1">Payment to be collected offline</p>
                )}
                {formData.paymentMethod === 'offline' && formData.paymentStatus === 'paid' && (
                  <p className="text-xs text-green-600 mt-1">✓ Payment already collected</p>
                )}
              </div>
              
              {/* WhatsApp Confirmation Option */}
              <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg border border-green-200">
                <input
                  type="checkbox"
                  id="sendWhatsAppConfirmation"
                  checked={formData.sendWhatsAppConfirmation}
                  onChange={(e) => setFormData({ ...formData, sendWhatsAppConfirmation: e.target.checked })}
                  className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                />
                <label htmlFor="sendWhatsAppConfirmation" className="flex items-center text-sm cursor-pointer">
                  <MessageCircle className="h-4 w-4 mr-2 text-green-600" />
                  Send WhatsApp booking confirmation
                </label>
              </div>

              {/* Email Confirmation Option */}
              <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  id="sendEmailConfirmation"
                  checked={formData.sendEmailConfirmation}
                  onChange={(e) => setFormData({ ...formData, sendEmailConfirmation: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="sendEmailConfirmation" className="flex items-center text-sm cursor-pointer">
                  <Mail className="h-4 w-4 mr-2 text-blue-600" />
                  Send email booking confirmation
                </label>
              </div>
              
              <div className="flex space-x-2">
                {mode === 'edit' && (
                  <Button type="button" variant="outline" onClick={() => setMode('view')} className="flex-1">
                    Cancel
                  </Button>
                )}
                <Button type="submit" className="flex-1" disabled={loading} data-testid="booking-submit-btn">
                  {loading ? 'Saving...' : mode === 'create' ? 'Create Booking' : 'Update Booking'}
                </Button>
              </div>
            </form>
          )}

          {/* View Mode */}
          {mode === 'view' && slotInfo.booking && (
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start">
                  <User className="h-4 w-4 mr-2 mt-1 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="font-semibold">{bookingDetails.customerName}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Mail className="h-4 w-4 mr-2 mt-1 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-semibold">{bookingDetails.customerEmail || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="h-4 w-4 mr-2 mt-1 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-semibold">{bookingDetails.customerPhone}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock className="h-4 w-4 mr-2 mt-1 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="font-semibold">{formatTime(bookingDetails.startTime)} - {formatTime(bookingDetails.endTime)}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <IndianRupee className="h-4 w-4 mr-2 mt-1 text-gray-500" />
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Payment Details</p>
                    <p className="font-semibold">Total: ₹{bookingDetails.amount}</p>
                    {bookingDetails.paymentStatus === 'partial' && (
                      <>
                        <p className="text-sm text-green-600">Paid: ₹{bookingDetails.amountPaid}{bookingDetails.paymentMethod === 'cash_offline' ? ' (Offline)' : ''}</p>
                        <p className="text-sm text-red-600 font-semibold">Pending: ₹{bookingDetails.amountPending}</p>
                      </>
                    )}
                    {bookingDetails.paymentStatus === 'paid' && (
                      <p className="text-sm text-green-600">{bookingDetails.paymentMethod === 'cash_offline' ? 'Fully Paid (Offline)' : 'Fully Paid'}</p>
                    )}
                    {bookingDetails.paymentStatus === 'pending' && (
                      <p className="text-sm text-red-600">Not Paid</p>
                    )}
                    {bookingDetails.paymentStatus === 'monthly' && (
                      <>
                        {Number(bookingDetails.amountPaid || 0) > 0 && (
                          <p className="text-sm text-green-600">Paid: ₹{bookingDetails.amountPaid}</p>
                        )}
                        <p className="text-sm text-amber-700 font-semibold">
                          Monthly Billing · Pending: ₹{bookingDetails.amountPending ?? bookingDetails.amount}
                        </p>
                        <p className="text-[11px] text-gray-500">Settle from <span className="font-medium">Bookings → Monthly Payments</span></p>
                      </>
                    )}
                    {bookingDetails.paymentStatus === 'refunded' && (
                      <>
                        <p className="text-sm text-green-600">Paid: ₹{bookingDetails.amountPaid || 0}</p>
                        <p className="text-sm text-red-600 font-semibold">Due: ₹{bookingDetails.amountPending ?? bookingDetails.amount}</p>
                        <p className="text-[11px] text-orange-700 font-medium">Payment was refunded — please collect manually.</p>
                      </>
                    )}
                    {['cash', 'upi', 'other'].includes(bookingDetails.paymentMethod) && Number(bookingDetails.amountPaid || 0) > 0 && paymentTrail.length === 0 && (
                      <p className="text-xs text-gray-600" data-testid="view-collected-via">
                        Collected via <span className="font-medium uppercase">{bookingDetails.paymentMethod}</span>
                      </p>
                    )}
                    {/* Full itemized collection trail — each rupee's method + collector + date */}
                    {paymentTrail.length > 0 && (
                      <div className="mt-2 text-xs border-t pt-1.5 space-y-1" data-testid="payment-trail">
                        <p className="font-semibold text-gray-600">Payment Trail</p>
                        {paymentTrail.map((p, i) => (
                          <div key={i} className="flex items-start justify-between gap-2" data-testid={`trail-row-${i}`}>
                            <span className="text-gray-600">
                              <span className={`font-medium ${p.isOnline ? 'text-blue-700' : 'text-emerald-700'}`}>{p.label}</span>
                              {p.isOnline && p.ref ? <> · <code className="bg-gray-100 px-1 rounded text-[10px]">{p.ref}</code></> : null}
                              {p.by ? <span className="text-gray-500"> · {p.by}</span> : null}
                              {p.at ? <span className="text-gray-500"> · {fmtTrailDate(p.at)}</span> : null}
                            </span>
                            <span className="font-semibold whitespace-nowrap" data-testid={`trail-amount-${i}`}>₹{p.amt}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between border-t pt-1 font-semibold text-gray-700">
                          <span>Total collected</span>
                          <span data-testid="trail-total">₹{trailTotal}{bookingDetails.paymentStatus === 'paid' ? ' · Fully paid' : ''}</span>
                        </div>
                      </div>
                    )}
                    {bookingDetails.rackRate > 0 && (
                      <div className="mt-1 text-xs text-gray-500 border-t pt-1">
                        <p>Rack Rate: ₹{bookingDetails.rackRate}</p>
                        {bookingDetails.venueDiscountPercent > 0 && <p>Venue Discount: {bookingDetails.venueDiscountPercent}%</p>}
                        {bookingDetails.platformDiscountPercent > 0 && <p>{bookingDetails.source === 'hudle' ? 'Hudle' : 'Playo'} Discount: {bookingDetails.platformDiscountPercent}%</p>}
                      </div>
                    )}
                    {(bookingDetails.razorpay_payment_id || bookingDetails.razorpayPaymentId) && (
                      <div className="mt-2 text-xs text-gray-500 border-t pt-1.5 space-y-0.5" data-testid="razorpay-refs">
                        <p className="font-semibold text-gray-600">Razorpay Reference{onlinePayments.length > 1 ? 's' : ''}</p>
                        {onlinePayments.length > 1 ? (
                          <div className="space-y-1.5" data-testid="razorpay-refs-list">
                            {onlinePayments.map((a, idx) => (
                              <div key={a.id || idx} className="space-y-0.5" data-testid={`razorpay-ref-${idx}`}>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-gray-500">₹{a.court_allocated}:</span>
                                  <code className="bg-gray-100 px-1 py-0.5 rounded text-[10px]">{a.razorpay_payment_id}</code>
                                  <button
                                    type="button"
                                    onClick={() => { navigator.clipboard.writeText(a.razorpay_payment_id); toast({ title: 'Copied', description: 'Payment ID copied' }); }}
                                    className="hover:bg-gray-100 rounded p-0.5"
                                    data-testid={`copy-payment-id-${idx}`}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </button>
                                </div>
                                {a.razorpay_bank_rrn && (
                                  <p className="ml-4 text-[10px]">UPI RRN: <code className="bg-gray-100 px-1 py-0.5 rounded">{a.razorpay_bank_rrn}</code></p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-500">Payment ID:</span>
                          <code className="bg-gray-100 px-1 py-0.5 rounded text-[10px]" data-testid="razorpay-payment-id">
                            {bookingDetails.razorpay_payment_id || bookingDetails.razorpayPaymentId}
                          </code>
                          {(onlinePayments[0]?.court_allocated || bookingDetails.amountPaid) > 0 && (
                            <span className="text-gray-600 font-medium" data-testid="razorpay-amount">— ₹{onlinePayments[0]?.court_allocated ?? bookingDetails.amountPaid}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(bookingDetails.razorpay_payment_id || bookingDetails.razorpayPaymentId);
                              toast({ title: 'Copied', description: 'Payment ID copied' });
                            }}
                            className="hover:bg-gray-100 rounded p-0.5"
                            data-testid="copy-payment-id"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                        )}
                        {onlinePayments.length <= 1 && (bookingDetails.razorpay_order_id || bookingDetails.razorpayOrderId) && (
                          <p>Order: <code className="bg-gray-100 px-1 py-0.5 rounded text-[10px]">{bookingDetails.razorpay_order_id || bookingDetails.razorpayOrderId}</code></p>
                        )}
                        {onlinePayments.length <= 1 && (bookingDetails.razorpay_bank_rrn || bookingDetails.razorpayBankRrn) && (
                          <p>UPI RRN: <code className="bg-gray-100 px-1 py-0.5 rounded text-[10px]">{bookingDetails.razorpay_bank_rrn || bookingDetails.razorpayBankRrn}</code></p>
                        )}
                        {onlinePayments.length <= 1 && bookingDetails.payment_method && (() => {
                          const raw = (bookingDetails.payment_method || '').toString();
                          const key = raw.toLowerCase();
                          const OFF = { cash: 'Offline Cash', cash_offline: 'Offline Cash', upi: 'Offline UPI', other: 'Offline Other' };
                          const ON = { upi: 'Online UPI (Razorpay)', card: 'Online Card (Razorpay)', netbanking: 'Online Netbanking (Razorpay)', wallet: 'Online Wallet (Razorpay)', emi: 'Online EMI (Razorpay)', online: 'Online (Razorpay)' };
                          const hasOnline = !!bookingDetails.razorpay_payment_id || hasOnlineAlloc;
                          let label;
                          if (key === 'online' || key.startsWith('online_')) {
                            label = ON[key.replace(/^online_/, '') || 'online'] || 'Online (Razorpay)';
                          } else if (hasOnline && ON[key]) {
                            label = ON[key];
                          } else {
                            label = OFF[key] || raw;
                          }
                          return <p>Method: <span>{label}</span></p>;
                        })()}
                      </div>
                    )}
                    {['paid', 'partial'].includes(bookingDetails.paymentStatus) && !(bookingDetails.razorpay_payment_id) && !hasOnlineAlloc && !['cash', 'upi', 'cash_offline', 'other'].includes((bookingDetails.paymentMethod || '').toLowerCase()) && (
                      <div className="mt-2 text-xs border-t pt-1.5">
                        <button
                          type="button"
                          onClick={handleFetchRefs}
                          disabled={fetchingRefs}
                          className="text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="fetch-razorpay-ref-button"
                        >
                          {fetchingRefs ? 'Fetching reference…' : 'Fetch Razorpay reference'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Booking Type</p>
                  <span className={`inline-block px-3 py-1 rounded text-xs font-semibold ${
                    bookingDetails.bookingType === 'event' ? 'bg-purple-100 text-purple-700' :
                    bookingDetails.bookingType === 'b2b' ? 'bg-orange-100 text-orange-700' :
                    bookingDetails.bookingType === 'sufc' ? 'bg-cyan-100 text-cyan-700' :
                    bookingDetails.bookingType === 'bcfc' ? 'bg-cyan-100 text-cyan-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {bookingDetails.bookingType?.toUpperCase() || 'B2C'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    bookingDetails.source === 'playo' ? 'bg-green-100 text-green-700' :
                    bookingDetails.source === 'hudle' ? 'bg-purple-100 text-purple-700' :
                    bookingDetails.source === 'online' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {bookingDetails.source === 'direct' ? 'ADMIN' : bookingDetails.source?.toUpperCase()}
                  </span>
                </div>
                {/* Booked By - shows who created the booking */}
                {bookingDetails.bookedByName && (
                  <div>
                    <p className="text-sm text-gray-500">Booked By</p>
                    <p className="font-medium text-gray-700">{bookingDetails.bookedByName}</p>
                  </div>
                )}
                {/* Created At - booking creation timestamp */}
                {bookingDetails.createdAt && (
                  <div>
                    <p className="text-sm text-gray-500">Created</p>
                    <p className="text-xs text-gray-600">
                      {new Date(bookingDetails.createdAt).toLocaleString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
              </div>

              {/* Refund summary — shown when a cancellation issued a refund */}
              {bookingDetails.status === 'cancelled' && (bookingDetails.refund_amount > 0 || bookingDetails.refund_status) && (
                <div
                  className="rounded-lg border border-orange-200 bg-orange-50 p-4 space-y-1"
                  data-testid="booking-detail-refund"
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-orange-800 text-sm">Refund</p>
                    <span
                      className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                        bookingDetails.refund_status === 'processed' ? 'bg-green-100 text-green-700' :
                        bookingDetails.refund_status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}
                      data-testid="booking-detail-refund-status"
                    >
                      {(bookingDetails.refund_status || 'processed').replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-orange-900 font-medium" data-testid="booking-detail-refund-amount">
                    Refunded ₹{bookingDetails.refund_amount}
                  </p>
                  {bookingDetails.refund_online > 0 && (
                    <p className="text-xs text-orange-700">Via Razorpay: ₹{bookingDetails.refund_online}</p>
                  )}
                  {bookingDetails.refund_manual_cash_pending > 0 && (
                    <p className="text-xs text-orange-700">Hand back as cash: ₹{bookingDetails.refund_manual_cash_pending}</p>
                  )}
                  {bookingDetails.refund_processed_at && (
                    <p className="text-xs text-orange-600">
                      {new Date(bookingDetails.refund_processed_at).toLocaleString('en-IN')}
                      {bookingDetails.refund_processed_by ? ` · by ${bookingDetails.refund_processed_by}` : ''}
                    </p>
                  )}
                  {(bookingDetails.payment_allocations || [])
                    .filter(a => a && a.type === 'refund' && a.razorpay_refund_id)
                    .map((a) => (
                      <p key={a.id || a.razorpay_refund_id} className="text-[11px] text-orange-500 break-all">
                        Refund ID: {a.razorpay_refund_id}
                      </p>
                    ))}
                </div>
              )}

              {/* Rentals / add-ons — counter staff (admin + venue managers) */}
              {canCollectPayments && slotInfo.booking && (
                <RentalsSection booking={slotInfo.booking} />
              )}

              {/* Action buttons - hidden for venue managers (read-only) */}
              {canEditBookings && bookingDetails.status !== 'cancelled' && (
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <Button onClick={() => setMode('edit')} variant="outline" className="flex-1">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    {bookingDetails.status === 'pending' && (
                      <Button onClick={handleConfirm} className="flex-1" disabled={loading}>
                        Confirm
                      </Button>
                    )}
                    <Button onClick={handleCancel} variant="destructive" className="flex-1" disabled={loading}>
                      Cancel
                    </Button>
                  </div>
                  {/* Reschedule — admin/superadmin only; blocked for aggregator bookings */}
                  {['admin', 'superadmin'].includes(user?.role) && bookingDetails.status !== 'cancelled' && (
                    isAggregatorBooking ? (
                      <p className="text-xs text-gray-500 flex items-center" data-testid="reschedule-blocked-note">
                        <CalendarClock className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                        Playo/Hudle bookings can't be rescheduled here — manage the change on the partner platform.
                      </p>
                    ) : (
                      <Button onClick={openReschedule} variant="outline" className="w-full border-[#0A2C93] text-[#0A2C93] hover:bg-[#0A2C93] hover:text-white" data-testid="reschedule-btn">
                        <CalendarClock className="h-4 w-4 mr-2" />
                        Reschedule Booking
                      </Button>
                    )
                  )}
                </div>
              )}

              {/* Payment collection actions — visible to venue managers too. Includes
                  'refunded' (e.g. QR-bounce auto-refund) so staff can collect manually. */}
              {canCollectPayments && bookingDetails.status !== 'cancelled' && (bookingDetails.paymentStatus === 'partial' || bookingDetails.paymentStatus === 'pending' || bookingDetails.paymentStatus === 'refunded') && (
                <div className="space-y-2">
                  {(bookingDetails.source === 'playo' || bookingDetails.source === 'hudle') && canEditBookings && (
                    <Button onClick={() => setMode('resolveDiscount')} className="w-full" variant="default" data-testid="resolve-discount-btn">
                      <IndianRupee className="h-4 w-4 mr-2" />
                      Resolve Discount
                    </Button>
                  )}
                  <Button onClick={() => setShowQRModal(true)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="collect-qr-btn">
                    <QrCode className="h-4 w-4 mr-2" />
                    Collect Payment via QR
                  </Button>
                  <Button onClick={() => setMode('collectPayment')} className="w-full" variant="secondary" data-testid="collect-offline-btn">
                    <IndianRupee className="h-4 w-4 mr-2" />
                    Collect Payment (Offline)
                  </Button>
                  <Button
                    onClick={() => setMode('sendPaymentLink')}
                    className="w-full"
                    variant="outline"
                    data-testid="send-payment-link-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Payment Link
                  </Button>
                </div>
              )}

              {/* Other admin-only actions (WhatsApp confirm etc.) */}
              {canEditBookings && bookingDetails.status !== 'cancelled' && (
                <div className="space-y-2">
                  {slotInfo.booking?.payment_link_sent && (
                  <div className="bg-green-50 p-2 rounded text-sm text-green-700 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Payment link already sent
                  </div>
                )}
                
                {/* WhatsApp Notification Button */}
                <Button 
                  onClick={async () => {
                    setSendingWhatsApp(true);
                    try {
                      await api.sendBookingConfirmation({
                        phone_number: bookingDetails.customerPhone,
                        customer_name: bookingDetails.customerName,
                        booking_id: slotInfo.booking.id,
                        date: slotInfo.date,
                        time: `${formatTime(bookingDetails.startTime)} - ${formatTime(bookingDetails.endTime)}`,
                        slot_type: bookingDetails.slotType,
                        amount: bookingDetails.amount
                      });
                      toast({
                        title: "WhatsApp Sent!",
                        description: `Booking confirmation sent to ${bookingDetails.customerPhone}`,
                      });
                    } catch (error) {
                      toast({
                        title: "WhatsApp Failed",
                        description: error.message || "Could not send WhatsApp message. Please ensure the number is on WhatsApp.",
                        variant: "destructive"
                      });
                    } finally {
                      setSendingWhatsApp(false);
                    }
                  }}
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={sendingWhatsApp}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {sendingWhatsApp ? 'Sending...' : 'Send WhatsApp Confirmation'}
                </Button>
              </div>
          )}
          
          {/* Read-only notice for venue managers */}
          {isReadOnly && (
            <div className="bg-amber-50 p-3 rounded-lg text-center mt-4">
              <p className="text-sm text-amber-800">
                <strong>View Only</strong> — You can collect payments via QR / Payment Link, but cannot edit or cancel bookings.
              </p>
            </div>
          )}
            </div>
          )}

          {/* Collect Payment Mode */}
          {mode === 'collectPayment' && slotInfo.booking && canCollectPayments && (
            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg space-y-2">
                <p className="font-semibold">Payment Summary</p>
                <p className="text-sm">Total Amount: ₹{bookingDetails.amount}</p>
                <p className="text-sm text-green-600">Already Paid: ₹{bookingDetails.amountPaid}</p>
                <p className="text-sm text-red-600 font-semibold">
                  Pending: ₹{bookingDetails.amountPending || bookingDetails.amount}
                </p>
              </div>
              
              <div>
                <Label htmlFor="collectAmount">Amount to Collect (₹)</Label>
                <Input
                  id="collectAmount"
                  type="number"
                  value={collectAmount}
                  onChange={(e) => setCollectAmount(parseFloat(e.target.value) || 0)}
                  placeholder="Enter amount to collect"
                  data-testid="collect-amount-input"
                />
              </div>

              <div>
                <Label className="text-sm">Collected via</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[['cash', 'Cash'], ['upi', 'UPI']].map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`collect-method-${val}`}
                      onClick={() => setCollectMethod(val)}
                      className={`p-2 border-2 rounded-lg text-sm font-medium ${collectMethod === val ? 'border-green-500 bg-green-50 text-[#0A2C93]' : 'border-gray-200 text-gray-600'}`}
                    >{lbl}</button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">For QR / card / online, use "Collect Payment via QR" instead — it records a Razorpay payment.</p>
              </div>

              <div className="flex space-x-2">
                <Button type="button" variant="outline" onClick={() => {setMode('view'); setCollectAmount(0); setCollectMethod('cash');}} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleCollectPayment} className="flex-1" disabled={loading} data-testid="collect-submit-btn">
                  {loading ? 'Processing...' : `Collect ₹${collectAmount || 0}`}
                </Button>
              </div>
            </div>
          )}

          {/* Send Payment Link Mode */}
          {mode === 'sendPaymentLink' && slotInfo.booking && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <p className="font-semibold">Send Payment Link</p>
                <p className="text-sm">Customer: {bookingDetails.customerName}</p>
                <p className="text-sm">Phone: {bookingDetails.customerPhone}</p>
                <p className="text-sm text-red-600 font-semibold">
                  Amount to Collect: ₹{bookingDetails.amountPending || bookingDetails.amount}
                </p>
              </div>
              
              <p className="text-sm text-gray-600">
                A Razorpay payment link will be sent to the customer via SMS. 
                Once the customer completes the payment, the booking will be automatically updated.
              </p>

              <div className="flex space-x-2">
                <Button type="button" variant="outline" onClick={() => setMode('view')} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    setSendingPaymentLink(true);
                    try {
                      const pendingAmount = bookingDetails.amountPending || bookingDetails.amount;
                      const result = await api.createPaymentLink({
                        booking_id: slotInfo.booking.id,
                        amount: pendingAmount,
                        customer_name: bookingDetails.customerName,
                        customer_email: bookingDetails.customerEmail || null,
                        customer_phone: bookingDetails.customerPhone,
                        description: `Payment for booking on ${slotInfo.date}`
                      });
                      
                      toast({
                        title: "Payment Link Sent!",
                        description: (
                          <div className="space-y-1">
                            <p>Link sent to {bookingDetails.customerPhone}</p>
                            <p className="text-xs text-gray-500 break-all">{result.short_url}</p>
                          </div>
                        ),
                      });
                      onClose();
                    } catch (error) {
                      toast({
                        title: "Failed to Send Payment Link",
                        description: error.message,
                        variant: "destructive"
                      });
                    } finally {
                      setSendingPaymentLink(false);
                    }
                  }} 
                  className="flex-1" 
                  disabled={sendingPaymentLink}
                >
                  {sendingPaymentLink ? 'Sending...' : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Link
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Resolve Discount Mode for Playo/Hudle */}
          {mode === 'resolveDiscount' && slotInfo.booking && canEditBookings && (
            <div className="space-y-4" data-testid="resolve-discount-panel">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm font-medium">Resolve Platform Discount</p>
                <p className="text-xs text-gray-600 mt-1">
                  Rack Rate: ₹{bookingDetails.amount} | Paid: ₹{bookingDetails.amountPaid} | Pending: ₹{bookingDetails.amountPending}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Discount Type</Label>
                <select
                  className="w-full mt-1 p-2 border rounded-lg text-sm"
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                  data-testid="discount-type-select"
                >
                  <option value="">Select type...</option>
                  <option value="venue">By Venue (we gave discount)</option>
                  <option value="platform">By {bookingDetails.source === 'hudle' ? 'Hudle' : 'Playo'} (platform absorbed)</option>
                  <option value="shared">Shared (both venue + platform)</option>
                </select>
              </div>

              {(discountType === 'venue' || discountType === 'shared') && (
                <div>
                  <Label className="text-sm">Venue Discount %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={venueDiscountPct}
                    onChange={(e) => setVenueDiscountPct(parseFloat(e.target.value) || 0)}
                    className="mt-1"
                    data-testid="venue-discount-input"
                  />
                </div>
              )}

              {(discountType === 'platform' || discountType === 'shared') && (
                <div>
                  <Label className="text-sm">{bookingDetails.source === 'hudle' ? 'Hudle' : 'Playo'} Discount %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={platformDiscountPct}
                    onChange={(e) => setPlatformDiscountPct(parseFloat(e.target.value) || 0)}
                    className="mt-1"
                    data-testid="platform-discount-input"
                  />
                </div>
              )}

              {discountType && (
                <div className="bg-green-50 p-3 rounded-lg text-sm">
                  <p className="font-medium">After resolving:</p>
                  <p>Adjusted Amount: ₹{Math.round(bookingDetails.amount * (1 - (discountType === 'platform' ? 0 : venueDiscountPct) / 100))}</p>
                  <p className="text-green-700 font-semibold">Status: Fully Paid</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setMode('view'); setDiscountType(''); setVenueDiscountPct(0); setPlatformDiscountPct(0); }} className="flex-1">
                  Cancel
                </Button>
                <Button
                  disabled={!discountType || loading}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await api.request(`/bookings/${slotInfo.booking.id}/resolve-discount`, {
                        method: 'POST',
                        body: JSON.stringify({
                          discount_type: discountType,
                          venue_discount_percent: discountType === 'platform' ? 0 : venueDiscountPct,
                          platform_discount_percent: discountType === 'venue' ? 0 : platformDiscountPct
                        })
                      });
                      toast({ title: "Discount Resolved", description: "Booking marked as fully paid." });
                      onClose();
                    } catch (error) {
                      toast({ title: "Error", description: error.message, variant: "destructive" });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="flex-1"
                  data-testid="resolve-discount-submit"
                >
                  {loading ? 'Resolving...' : 'Resolve & Mark Paid'}
                </Button>
              </div>
            </div>
          )}

          {/* Reschedule panel */}
          {mode === 'reschedule' && slotInfo.booking && (
            <div className="space-y-4" data-testid="reschedule-panel">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-[#0A2C93]">Move this booking to a new slot</p>
                <p className="text-xs text-gray-600 mt-1">
                  Payment history &amp; rentals stay with the booking. Price is recalculated for the new slot.
                </p>
              </div>

              <div>
                <Label className="text-sm">New Date</Label>
                <Input type="date" value={reschedForm?.date || ''}
                  onChange={(e) => setReschedForm(f => ({ ...f, date: e.target.value }))}
                  className="mt-1" data-testid="reschedule-date" />
              </div>

              <div>
                <Label className="text-sm">Court / Ground</Label>
                <select className="w-full mt-1 p-2 border rounded-lg text-sm"
                  value={reschedForm?.courtId || ''}
                  onChange={(e) => setReschedForm(f => ({ ...f, courtId: e.target.value }))}
                  data-testid="reschedule-court">
                  {(currentFacility?.courts || []).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Start Time</Label>
                  <select className="w-full mt-1 p-2 border rounded-lg text-sm"
                    value={reschedForm?.startTime || ''}
                    onChange={(e) => setReschedForm(f => ({ ...f, startTime: e.target.value }))}
                    data-testid="reschedule-start">
                    {TIME_SLOTS.map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Duration (30-min slots)</Label>
                  <Input type="number" min="1" value={reschedForm?.duration || 1}
                    onChange={(e) => setReschedForm(f => ({ ...f, duration: parseInt(e.target.value) || 1 }))}
                    className="mt-1" data-testid="reschedule-duration" />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                New slot: {reschedForm?.startTime && reschedEndTime
                  ? `${formatTime(reschedForm.startTime)} – ${formatTime(reschedEndTime)}` : '—'}
              </p>

              {reschedLoading && <p className="text-xs text-gray-500">Checking availability &amp; price…</p>}
              {reschedPreview && (
                <div className={`p-3 rounded-lg text-sm ${reschedPreview.available ? 'bg-green-50' : 'bg-red-50'}`} data-testid="reschedule-preview">
                  {!reschedPreview.available ? (
                    <p className="text-red-700 font-medium">Not available: {reschedPreview.conflict_reason}</p>
                  ) : (
                    <>
                      <p className="text-gray-500 line-through">
                        {reschedPreview.old.when} · {reschedPreview.old.court} · ₹{reschedPreview.old.amount}
                      </p>
                      <div className="flex justify-between font-semibold text-[#0A2C93] mt-1">
                        <span>{reschedPreview.new.when} · {reschedPreview.new.court}</span>
                        <span>₹{reschedPreview.new.amount}</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200 space-y-0.5">
                        <div className="flex justify-between text-gray-600"><span>Already paid</span><span>₹{reschedPreview.amount_paid}</span></div>
                        {reschedPreview.new_pending > 0 && (
                          <div className="flex justify-between text-amber-700 font-medium"><span>Balance to collect</span><span>₹{reschedPreview.new_pending}</span></div>
                        )}
                        {reschedPreview.credit_due > 0 && (
                          <div className="flex justify-between text-amber-700 font-medium" data-testid="reschedule-credit">
                            <span>Credit due to customer</span><span>₹{reschedPreview.credit_due}</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div>
                <Label className="text-sm">
                  Reason {reschedPreview?.reason_required && <span className="text-red-600 font-medium">(required — date changed)</span>}
                </Label>
                <textarea rows={2} className="w-full mt-1 p-2 border rounded-lg text-sm"
                  value={reschedForm?.reason || ''}
                  onChange={(e) => setReschedForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Why is this booking being moved?" data-testid="reschedule-reason" />
              </div>

              {bookingDetails.customerEmail && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={reschedForm?.notify || false}
                    onChange={(e) => setReschedForm(f => ({ ...f, notify: e.target.checked }))}
                    data-testid="reschedule-notify" />
                  Email the customer about this change
                </label>
              )}
              {reschedForm?.notify && (
                <textarea rows={2} className="w-full p-2 border rounded-lg text-sm"
                  value={reschedForm?.message || ''}
                  onChange={(e) => setReschedForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Optional note to include in the email" data-testid="reschedule-message" />
              )}

              {reschedError && (
                <p className="text-xs text-red-600 flex items-center" data-testid="reschedule-error">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />{reschedError}
                </p>
              )}

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1"
                  onClick={() => { setMode('view'); setReschedForm(null); setReschedPreview(null); setReschedError(''); }}>
                  Cancel
                </Button>
                <Button onClick={confirmReschedule}
                  disabled={reschedLoading || !reschedPreview?.available}
                  className="flex-1 bg-[#0A2C93] hover:bg-[#081f6b] text-white"
                  data-testid="reschedule-confirm">
                  {reschedLoading ? 'Working…' : 'Confirm Reschedule'}
                </Button>
              </div>
            </div>
          )}


          {/* Blocked Slot */}
          {slotInfo.status === 'blocked' && (
            <div className="text-center py-6">
              <p className="text-gray-600">This slot is blocked for bookings.</p>
              {slotInfo.reason && <p className="text-sm text-gray-500 mt-2">Reason: {slotInfo.reason}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowCancelDialog(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()} data-testid="cancel-booking-dialog">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {hasCollected ? 'Cancel & Refund Booking' : 'Cancel Booking'}
            </h3>

            {hasCollected ? (
              <>
                <p className="text-sm text-gray-600 mb-1">
                  Collected <span className="font-semibold">₹{amountPaidForRefund.toLocaleString('en-IN')}</span>
                  {refundPreview && (
                    <> — <span className="font-medium">₹{refundPreview.online_collected} online</span>, <span className="font-medium">₹{refundPreview.cash_collected} cash</span></>
                  )}.
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Refund % is of the amount COLLECTED. Razorpay refunds only the online portion; any excess is a manual cash refund.
                </p>

                <div className="mb-4" data-testid="refund-percent-block">
                  <label className="block text-xs font-medium text-gray-700 mb-2">Refund percentage</label>
                  <div className="flex items-center gap-2 mb-2">
                    {[0, 25, 50, 100].map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setRefundPercent(p)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md border transition ${
                          refundPercent === p
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`refund-chip-${p}`}
                      >
                        {p}%
                      </button>
                    ))}
                    <div className="flex items-center gap-1 ml-2">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={refundPercent}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (Number.isNaN(v)) setRefundPercent(0);
                          else setRefundPercent(Math.max(0, Math.min(100, v)));
                        }}
                        className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-md"
                        data-testid="refund-percent-input"
                      />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                  </div>
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs">
                    {(() => {
                      const target = Math.round(amountPaidForRefund * refundPercent) / 100;
                      const online = refundPreview?.online_collected ?? amountPaidForRefund;
                      const via = Math.min(target, online);
                      const manual = Math.round((target - via) * 100) / 100;
                      if (target <= 0) {
                        return <span className="text-gray-700"><span className="font-semibold">No refund</span> — booking will just be cancelled.</span>;
                      }
                      return (
                        <span className="text-amber-800">
                          Refunding <span className="font-semibold">₹{via.toLocaleString('en-IN')}</span> via Razorpay
                          {manual > 0 && <> · <span className="font-semibold">₹{manual.toLocaleString('en-IN')}</span> collected in cash — hand back manually</>}.
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-600 mb-4">Are you sure you want to cancel this booking?</p>
            )}

            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg mb-4">
              <input
                type="checkbox"
                id="sendCancellationEmail"
                checked={sendCancellationEmail}
                onChange={(e) => setSendCancellationEmail(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="sendCancellationEmail" className="text-sm cursor-pointer">
                Send cancellation email to customer
              </label>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(false)}
                className="flex-1"
                disabled={loading}
                data-testid="cancel-dialog-keep-btn"
              >
                Keep Booking
              </Button>
              <Button
                variant="destructive"
                onClick={confirmCancellation}
                className="flex-1"
                disabled={loading}
                data-testid="cancel-dialog-confirm-btn"
              >
                {loading
                  ? (hasCollected && refundPercent > 0 ? 'Refunding…' : 'Cancelling…')
                  : hasCollected && refundPercent > 0
                    ? `Cancel & Refund ₹${refundAmount.toLocaleString('en-IN')}`
                    : hasCollected
                      ? 'Cancel Without Refund'
                      : 'Yes, Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* QR Payment Modal — venue Razorpay */}
      {showQRModal && bookingDetails && (
        <QRPaymentModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          bookingId={bookingDetails.id || bookingDetails.bookingId || slotInfo?.booking?.id}
          amountDue={Number(bookingDetails.amountPending || bookingDetails.amount_pending || bookingDetails.amount || 0)}
          onPaid={() => {
            setShowQRModal(false);
            // Reload booking via existing context refresh
            if (typeof onClose === 'function') onClose();
          }}
        />
      )}
    </div>
  );
};

export default BookingDialog;
